import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Search, Plus, GitCompare, ChevronDown, ChevronUp, Globe, Shield, Zap, Clock,
  Activity, Server, CheckCircle, AlertTriangle, XCircle, ExternalLink, X, ArrowUpDown,
} from "lucide-react";

const PROVIDERS = [
  { id: "openai", name: "OpenAI", status: "operational", uptime: 99.97, latency: 42, errorRate: 0.02, models: ["gpt-5.2", "gpt-4o", "gpt-4o-mini"], tier: "Tier 0", region: "US-East", rps: 12400, monthlyRequests: "14.2M",
    uptimeSegments: generateUptimeSegments(99.97) },
  { id: "anthropic", name: "Anthropic", status: "operational", uptime: 99.94, latency: 51, errorRate: 0.04, models: ["claude-sonnet-4", "claude-haiku-4"], tier: "Tier 0", region: "US-West", rps: 8700, monthlyRequests: "9.8M",
    uptimeSegments: generateUptimeSegments(99.94) },
  { id: "google", name: "Google AI", status: "operational", uptime: 99.91, latency: 38, errorRate: 0.06, models: ["gemini-3-flash", "gemini-3-pro"], tier: "Tier 0", region: "Global", rps: 15200, monthlyRequests: "18.1M",
    uptimeSegments: generateUptimeSegments(99.91) },
  { id: "moonshot", name: "Moonshot AI", status: "operational", uptime: 99.82, latency: 67, errorRate: 0.11, models: ["kimi-k2.5"], tier: "Tier 1", region: "Asia-Pacific", rps: 3200, monthlyRequests: "2.1M",
    uptimeSegments: generateUptimeSegments(99.82) },
  { id: "cohere", name: "Cohere", status: "degraded", uptime: 98.76, latency: 89, errorRate: 0.42, models: ["command-r-plus", "command-r"], tier: "Tier 1", region: "US-East", rps: 2100, monthlyRequests: "1.4M",
    uptimeSegments: generateUptimeSegments(98.76) },
  { id: "mistral", name: "Mistral AI", status: "operational", uptime: 99.89, latency: 44, errorRate: 0.08, models: ["mistral-large", "mistral-medium"], tier: "Tier 0", region: "EU-West", rps: 6800, monthlyRequests: "7.3M",
    uptimeSegments: generateUptimeSegments(99.89) },
  { id: "together", name: "Together AI", status: "operational", uptime: 99.85, latency: 35, errorRate: 0.09, models: ["llama-3.1-405b", "llama-3.1-70b"], tier: "Tier 1", region: "US-West", rps: 4500, monthlyRequests: "3.9M",
    uptimeSegments: generateUptimeSegments(99.85) },
  { id: "perplexity", name: "Perplexity", status: "operational", uptime: 99.78, latency: 73, errorRate: 0.15, models: ["sonar-pro", "sonar"], tier: "Tier 1", region: "US-East", rps: 1900, monthlyRequests: "1.1M",
    uptimeSegments: generateUptimeSegments(99.78) },
  { id: "deepseek", name: "DeepSeek", status: "operational", uptime: 99.71, latency: 82, errorRate: 0.19, models: ["deepseek-v3", "deepseek-coder"], tier: "Tier 1", region: "Asia-Pacific", rps: 2800, monthlyRequests: "1.8M",
    uptimeSegments: generateUptimeSegments(99.71) },
  { id: "self-hosted", name: "Self-Hosted (Gemma 4)", status: "operational", uptime: 99.99, latency: 12, errorRate: 0.01, models: ["gemma-4-27b-it", "gemma-4-e4b-it"], tier: "Tier 2", region: "On-Prem", rps: 800, monthlyRequests: "0.4M",
    uptimeSegments: generateUptimeSegments(99.99) },
];

const PROVIDER_DOCS = {
  openai: "https://platform.openai.com/docs",
  anthropic: "https://docs.anthropic.com",
  google: "https://ai.google.dev/docs",
  moonshot: "https://platform.moonshot.cn/docs",
  cohere: "https://docs.cohere.com",
  mistral: "https://docs.mistral.ai",
  together: "https://docs.together.ai",
  perplexity: "https://docs.perplexity.ai",
  deepseek: "https://platform.deepseek.com/api-docs",
  "self-hosted": null,
};

function generateUptimeSegments(overallUptime) {
  const segments = [];
  const now = Date.now();
  for (let i = 47; i >= 0; i--) {
    const time = new Date(now - i * 30 * 60 * 1000);
    const rand = Math.random();
    let status = "up";
    if (overallUptime < 99) {
      if (rand < 0.06) status = "degraded";
      if (rand < 0.02) status = "down";
    } else if (overallUptime < 99.9) {
      if (rand < 0.03) status = "degraded";
      if (rand < 0.005) status = "down";
    } else {
      if (rand < 0.01) status = "degraded";
    }
    segments.push({ time: time.toISOString(), status, hour: time.getHours() + ":" + String(time.getMinutes()).padStart(2, "0") });
  }
  return segments;
}

function UptimeBar({ segments }) {
  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex gap-[1px] h-6 items-end">
        {segments.map((seg, i) => (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <div
                className={`w-[5px] rounded-sm transition-all hover:scale-y-110 cursor-pointer ${
                  seg.status === "up" ? "bg-emerald-500 h-5" : seg.status === "degraded" ? "bg-amber-500 h-4" : "bg-red-500 h-3"
                }`}
              />
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <p>{seg.hour} — {seg.status === "up" ? "Operational" : seg.status === "degraded" ? "Degraded" : "Outage"}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}

function StatusIcon({ status }) {
  if (status === "operational") return <CheckCircle className="w-4 h-4 text-emerald-500" />;
  if (status === "degraded") return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <XCircle className="w-4 h-4 text-red-500" />;
}

export default function ProviderManagement() {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("uptime");
  const [expandedId, setExpandedId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [newProvider, setNewProvider] = useState({ name: "", endpoint: "", tier: "Tier 1", region: "US-East" });

  const sorted = useMemo(() => {
    let list = PROVIDERS.filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.models.some((m) => m.toLowerCase().includes(search.toLowerCase()))
    );
    list.sort((a, b) => {
      if (sortBy === "uptime") return b.uptime - a.uptime;
      if (sortBy === "latency") return a.latency - b.latency;
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "rps") return b.rps - a.rps;
      return 0;
    });
    return list;
  }, [search, sortBy]);

  const compareProviders = useMemo(() => PROVIDERS.filter((p) => compareIds.includes(p.id)), [compareIds]);

  const toggleCompare = (id) => {
    setCompareIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 3 ? [...prev, id] : prev);
  };

  const summaryStats = useMemo(() => ({
    total: PROVIDERS.length,
    operational: PROVIDERS.filter((p) => p.status === "operational").length,
    avgUptime: (PROVIDERS.reduce((s, p) => s + p.uptime, 0) / PROVIDERS.length).toFixed(2),
    avgLatency: Math.round(PROVIDERS.reduce((s, p) => s + p.latency, 0) / PROVIDERS.length),
  }), []);

  return (
    <div className="space-y-6" data-testid="provider-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-[var(--qn-foreground)]">Provider Management</h1>
          <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">Monitor, compare, and manage your AI provider fleet</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant={compareMode ? "default" : "outline"}
            size="sm"
            onClick={() => { setCompareMode(!compareMode); setCompareIds([]); }}
            data-testid="compare-mode-btn"
            className={compareMode ? "bg-[var(--qn-primary)] text-white" : ""}
          >
            <GitCompare className="w-4 h-4 mr-2" />
            {compareMode ? `Comparing (${compareIds.length}/3)` : "Compare"}
          </Button>
          <Button size="sm" onClick={() => setAddModalOpen(true)} data-testid="add-provider-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Provider
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Total Providers", value: summaryStats.total, icon: Server, color: "var(--qn-primary)" },
          { label: "Operational", value: summaryStats.operational, icon: CheckCircle, color: "#10b981" },
          { label: "Avg Uptime", value: summaryStats.avgUptime + "%", icon: Shield, color: "#8b5cf6" },
          { label: "Avg Latency", value: summaryStats.avgLatency + "ms", icon: Clock, color: "#f59e0b" },
        ].map((card) => (
          <Card key={card.label} className="bg-[var(--qn-card)] border-[var(--qn-border)]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: card.color + "15" }}>
                <card.icon className="w-5 h-5" style={{ color: card.color }} />
              </div>
              <div>
                <div className="text-xs text-[var(--qn-foreground-muted)]">{card.label}</div>
                <div className="text-xl font-bold font-outfit text-[var(--qn-foreground)]">{card.value}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Sort */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-muted)]" />
          <Input
            placeholder="Search providers or models..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[var(--qn-surface)] border-[var(--qn-border)]"
            data-testid="provider-search"
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-44 bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="provider-sort">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="uptime">Sort by Uptime</SelectItem>
            <SelectItem value="latency">Sort by Latency</SelectItem>
            <SelectItem value="name">Sort by Name</SelectItem>
            <SelectItem value="rps">Sort by RPS</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Provider List */}
      <div className="space-y-3">
        {sorted.map((provider, idx) => {
          const isExpanded = expandedId === provider.id;
          const isCompareSelected = compareIds.includes(provider.id);
          return (
            <motion.div
              key={provider.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
            >
              <Card className={`bg-[var(--qn-card)] border transition-all duration-200 ${
                isCompareSelected ? "border-[var(--qn-primary)] ring-1 ring-[var(--qn-primary)]/20" : "border-[var(--qn-border)] hover:border-[var(--qn-primary)]/30"
              }`} data-testid={`provider-row-${provider.id}`}>
                <CardContent className="p-0">
                  {/* Main Row */}
                  <div
                    className="flex items-center gap-4 px-5 py-4 cursor-pointer"
                    onClick={() => compareMode ? toggleCompare(provider.id) : setExpandedId(isExpanded ? null : provider.id)}
                  >
                    {compareMode && (
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isCompareSelected ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]" : "border-[var(--qn-border)]"
                      }`}>
                        {isCompareSelected && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                    )}
                    <StatusIcon status={provider.status} />
                    <div className="min-w-[140px]">
                      <span className="font-medium text-sm text-[var(--qn-foreground)]">{provider.name}</span>
                      <div className="text-xs text-[var(--qn-foreground-muted)]">{provider.region}</div>
                    </div>
                    <div className="flex-1">
                      <UptimeBar segments={provider.uptimeSegments} />
                    </div>
                    <div className="text-right min-w-[80px]">
                      <span className={`text-sm font-mono font-bold ${provider.uptime >= 99.9 ? "text-emerald-500" : provider.uptime >= 99 ? "text-amber-500" : "text-red-500"}`}>
                        {provider.uptime}%
                      </span>
                      <div className="text-[10px] text-[var(--qn-foreground-subtle)]">uptime</div>
                    </div>
                    <div className="text-right min-w-[60px]">
                      <span className="text-sm font-mono text-[var(--qn-foreground)]">{provider.latency}ms</span>
                      <div className="text-[10px] text-[var(--qn-foreground-subtle)]">p50</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-[var(--qn-border)]">{provider.tier}</Badge>
                    {!compareMode && (
                      <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                        <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" />
                      </motion.div>
                    )}
                  </div>

                  {/* Expanded Detail */}
                  <AnimatePresence>
                    {isExpanded && !compareMode && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-4 pt-2 border-t border-[var(--qn-border)] grid grid-cols-4 gap-6">
                          <div>
                            <div className="text-xs text-[var(--qn-foreground-muted)] mb-2">Supported Models</div>
                            <div className="flex flex-wrap gap-1.5">
                              {provider.models.map((m) => (
                                <Badge key={m} variant="secondary" className="text-[10px] bg-[var(--qn-surface)] border-[var(--qn-border)]">{m}</Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-[var(--qn-foreground-muted)] mb-2">Performance</div>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-xs">
                                <span className="text-[var(--qn-foreground-muted)]">Error Rate</span>
                                <span className={`font-mono ${provider.errorRate < 0.1 ? "text-emerald-500" : "text-amber-500"}`}>{provider.errorRate}%</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-[var(--qn-foreground-muted)]">RPS</span>
                                <span className="font-mono text-[var(--qn-foreground)]">{provider.rps.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-[var(--qn-foreground-muted)] mb-2">Traffic</div>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-xs">
                                <span className="text-[var(--qn-foreground-muted)]">Monthly Requests</span>
                                <span className="font-mono text-[var(--qn-foreground)]">{provider.monthlyRequests}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-[var(--qn-foreground-muted)]">Region</span>
                                <span className="text-[var(--qn-foreground)]">{provider.region}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              data-testid={`provider-docs-${provider.id}`}
                              onClick={() => {
                                const url = PROVIDER_DOCS[provider.id];
                                if (url) window.open(url, "_blank", "noopener");
                                else window.__qnnealSetActivePage?.("docs");
                              }}
                            >
                              <ExternalLink className="w-3 h-3 mr-1.5" /> API Docs
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              data-testid={`provider-logs-${provider.id}`}
                              onClick={() => window.__qnnealSetActivePage?.("status")}
                            >
                              <Activity className="w-3 h-3 mr-1.5" /> View Logs
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Compare Panel */}
      <AnimatePresence>
        {compareMode && compareProviders.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <Card className="bg-[var(--qn-card)] border-[var(--qn-primary)]/30" data-testid="compare-panel">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-outfit flex items-center gap-2">
                  <GitCompare className="w-5 h-5 text-[var(--qn-primary)]" />
                  Provider Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--qn-border)]">
                        <th className="text-left py-2 px-3 text-[var(--qn-foreground-muted)] font-medium">Metric</th>
                        {compareProviders.map((p) => (
                          <th key={p.id} className="text-center py-2 px-3 text-[var(--qn-foreground)] font-medium">{p.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: "Status", key: "status", render: (v) => <Badge variant={v === "operational" ? "default" : "destructive"} className="text-[10px]">{v}</Badge> },
                        { label: "Uptime", key: "uptime", render: (v) => <span className={`font-mono ${v >= 99.9 ? "text-emerald-500" : "text-amber-500"}`}>{v}%</span> },
                        { label: "Latency (p50)", key: "latency", render: (v) => <span className="font-mono">{v}ms</span> },
                        { label: "Error Rate", key: "errorRate", render: (v) => <span className={`font-mono ${v < 0.1 ? "text-emerald-500" : "text-amber-500"}`}>{v}%</span> },
                        { label: "RPS", key: "rps", render: (v) => <span className="font-mono">{v.toLocaleString()}</span> },
                        { label: "Region", key: "region" },
                        { label: "Tier", key: "tier" },
                        { label: "Models", key: "models", render: (v) => <span className="text-xs">{v.join(", ")}</span> },
                      ].map((row) => (
                        <tr key={row.label} className="border-b border-[var(--qn-border)]/50">
                          <td className="py-2.5 px-3 text-[var(--qn-foreground-muted)]">{row.label}</td>
                          {compareProviders.map((p) => (
                            <td key={p.id} className="text-center py-2.5 px-3 text-[var(--qn-foreground)]">
                              {row.render ? row.render(p[row.key]) : p[row.key]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Provider Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="add-provider-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit">Add New Provider</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-[var(--qn-foreground-muted)] mb-1 block">Provider Name</label>
              <Input
                placeholder="e.g., Custom LLM Provider"
                value={newProvider.name}
                onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })}
                className="bg-[var(--qn-surface)] border-[var(--qn-border)]"
                data-testid="add-provider-name"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--qn-foreground-muted)] mb-1 block">API Endpoint</label>
              <Input
                placeholder="https://api.example.com/v1"
                value={newProvider.endpoint}
                onChange={(e) => setNewProvider({ ...newProvider, endpoint: e.target.value })}
                className="bg-[var(--qn-surface)] border-[var(--qn-border)]"
                data-testid="add-provider-endpoint"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[var(--qn-foreground-muted)] mb-1 block">Tier</label>
                <Select value={newProvider.tier} onValueChange={(v) => setNewProvider({ ...newProvider, tier: v })}>
                  <SelectTrigger className="bg-[var(--qn-surface)] border-[var(--qn-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tier 0">Tier 0 - OpenAI Compatible</SelectItem>
                    <SelectItem value="Tier 1">Tier 1 - Transform Adapter</SelectItem>
                    <SelectItem value="Tier 2">Tier 2 - Enterprise/Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[var(--qn-foreground-muted)] mb-1 block">Region</label>
                <Select value={newProvider.region} onValueChange={(v) => setNewProvider({ ...newProvider, region: v })}>
                  <SelectTrigger className="bg-[var(--qn-surface)] border-[var(--qn-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US-East">US-East</SelectItem>
                    <SelectItem value="US-West">US-West</SelectItem>
                    <SelectItem value="EU-West">EU-West</SelectItem>
                    <SelectItem value="Asia-Pacific">Asia-Pacific</SelectItem>
                    <SelectItem value="Global">Global</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModalOpen(false)}>Cancel</Button>
            <Button onClick={() => setAddModalOpen(false)} data-testid="add-provider-submit">Add Provider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
